import type { AppRouter } from "@repo/vihangam-yog-backend/router";
import { PARTY_MAX } from "@repo/vihangam-yog-shared";
import type { inferRouterInputs } from "@trpc/server";

/** "wheelchair": uses a wheelchair. "chair": cannot sit on the floor. */
export type PartyNeed = "wheelchair" | "chair";

/** The gender values the backend's `registerEventBundle` accepts. */
type BundleGender = NonNullable<
  inferRouterInputs<AppRouter>["registerEventBundle"]["party"][number]["gender"]
>;

/** The three options, in the order the chips render. Checked against the
 *  backend's enum so a value the form offers is one the server takes. */
export const PARTY_GENDERS = [
  "female",
  "male",
  "other",
] as const satisfies readonly BundleGender[];

export type PartyGender = (typeof PARTY_GENDERS)[number];

export interface PartyMember {
  name: string;
  kind: "adult" | "child";
  /** Kept as typed text in the draft; parsed to a number at submit. */
  age: string;
  needs: PartyNeed[];
  /** Optional; null until chosen. Asked so seating can be planned. */
  gender: PartyGender | null;
}

const NEED_LABELS = {
  wheelchair: "Will come on a wheelchair",
  chair: "Can't sit on the floor",
} satisfies Record<PartyNeed, string>;

const GENDER_LABELS = {
  female: "Female",
  male: "Male",
  other: "Other",
} satisfies Record<PartyGender, string>;

/** The two access needs, in the order the chips render. */
const PARTY_NEEDS: readonly PartyNeed[] = ["wheelchair", "chair"];

/** A fresh, empty row of the given kind. */
export function emptyMember(kind: PartyMember["kind"]): PartyMember {
  return { name: "", kind, age: "", needs: [], gender: null };
}

interface PartyEditorProps {
  party: PartyMember[];
  onChange: (next: PartyMember[]) => void;
  disabled?: boolean;
  /** The contact's name from the fields above; shown on the first row
   *  when they are coming, so it is typed once. */
  selfName?: string;
  /** When true the first row is the person filling the form. */
  selfIncluded?: boolean;
  onSelfIncludedChange?: (next: boolean) => void;
  /** The submit problem, if it points at one of these rows. */
  problem?: { target: string; message: string } | null;
}

/** Adults and children by name; the counts derive from the list. */
export function PartyEditor({
  party,
  onChange,
  disabled,
  selfName = "",
  selfIncluded = false,
  onSelfIncludedChange,
  problem = null,
}: PartyEditorProps) {
  const adults = party.filter((p) => p.kind === "adult").length;
  const full = party.length >= PARTY_MAX;
  const update = (i: number, patch: Partial<PartyMember>) =>
    onChange(party.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const toggleNeed = (i: number, need: PartyNeed) => {
    const row = party[i];
    if (!row) {
      return;
    }
    const needs = row.needs.includes(need)
      ? row.needs.filter((n) => n !== need)
      : [...row.needs, need];
    update(i, { needs });
  };
  const remove = (i: number) => onChange(party.filter((_, idx) => idx !== i));
  const add = (kind: PartyMember["kind"]) =>
    onChange([...party, emptyMember(kind)]);
  // Leaving the party drops the self row; coming back puts a fresh adult
  // row first. Either way at least one adult row stays on the form.
  const setSelf = (next: boolean) => {
    if (next === selfIncluded) {
      return;
    }
    if (next) {
      onChange([emptyMember("adult"), ...party]);
    } else {
      const rest = party.slice(1);
      onChange(rest.length > 0 ? rest : [emptyMember("adult")]);
    }
    onSelfIncludedChange?.(next);
  };

  let adultN = 0;
  let childN = 0;
  return (
    <fieldset
      id="reg-party"
      className="rounded-2xl border border-hairline bg-card p-6 md:p-7"
    >
      {/* A bordered fieldset cuts its border behind a visible <legend>, which
          reads as a rendering glitch. Keep the legend for assistive tech and
          draw the heading as ordinary text. */}
      <legend className="sr-only">Who is coming</legend>
      {problem?.target === "reg-party" ? (
        <p className="mt-1.5 text-sm text-crimson">{problem.message}</p>
      ) : null}
      <p className="mt-1.5 text-xs leading-relaxed text-ink-soft">
        {selfIncluded
          ? "You are listed first, from the name above. Add everyone coming with you."
          : "List everyone coming."}{" "}
        Ages and gender help us plan seating and food.
      </p>
      <ul className="mt-5 space-y-4">
        {party.map((p, i) => {
          const n = p.kind === "adult" ? ++adultN : ++childN;
          const self = selfIncluded && i === 0;
          const who = self ? "you" : `${p.kind} ${n}`;
          const label = `Name of ${who}`;
          const canRemove = !self && !(p.kind === "adult" && adults === 1);
          return (
            <li
              key={`${p.kind}-${i}`}
              className="grid gap-3 border-b border-hairline pb-4 last:border-b-0 last:pb-0"
            >
              {/* Name on its own line; age and gender on the next; the two
                  access boxes on the last. */}
              <div className="flex items-center gap-3">
                {self ? (
                  <p
                    className="flex-1 text-sm text-ink"
                    data-testid="party-self"
                  >
                    <span className="font-semibold">
                      {selfName.trim() || "You"}
                    </span>
                    {selfName.trim() ? (
                      <span className="text-ink-soft"> (you, from above)</span>
                    ) : (
                      <span className="text-ink-soft">
                        {" "}
                        (your name comes from the field above)
                      </span>
                    )}
                  </p>
                ) : (
                  <input
                    id={`party-name-${i}`}
                    className="field flex-1"
                    aria-label={label}
                    placeholder={label}
                    aria-invalid={
                      problem?.target === `party-name-${i}` ? true : undefined
                    }
                    value={p.name}
                    disabled={disabled}
                    onChange={(e) => update(i, { name: e.target.value })}
                    maxLength={120}
                  />
                )}
                {/* A chip, not an orphaned word floating beside the input. */}
                <span className="w-16 shrink-0 rounded-full bg-ivory-deep px-2.5 py-1 text-center text-[0.68rem] font-semibold tracking-wide text-ink-soft uppercase">
                  {p.kind}
                </span>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  disabled={disabled}
                  aria-label={`Remove ${p.name || who}`}
                  className={`flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full text-lg transition-colors ${canRemove ? "text-ink-soft hover:bg-ivory-deep hover:text-crimson" : "pointer-events-none opacity-0"}`}
                  tabIndex={canRemove ? undefined : -1}
                  aria-hidden={canRemove ? undefined : true}
                >
                  ×
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  id={`party-age-${i}`}
                  className="field w-20! shrink-0"
                  type="number"
                  aria-invalid={
                    problem?.target === `party-age-${i}` ? true : undefined
                  }
                  inputMode="numeric"
                  min={p.kind === "child" ? 0 : 16}
                  max={p.kind === "child" ? 15 : 120}
                  aria-label={`Age of ${who}`}
                  placeholder="Age"
                  value={p.age}
                  disabled={disabled}
                  onChange={(e) => update(i, { age: e.target.value })}
                />
                <div
                  role="radiogroup"
                  aria-label={`Gender of ${who}`}
                  className="flex flex-wrap items-center gap-1.5"
                >
                  {PARTY_GENDERS.map((g) => {
                    const on = p.gender === g;
                    return (
                      <label
                        key={g}
                        className={`flex min-h-11 cursor-pointer items-center rounded-full border px-3.5 text-sm transition-colors has-disabled:cursor-not-allowed has-disabled:opacity-50 has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-teal-deep ${on ? "border-magenta bg-magenta/5 font-medium text-ink" : "border-hairline-strong text-ink-soft hover:border-ink hover:text-ink"}`}
                      >
                        <input
                          type="radio"
                          name={`gender-${i}`}
                          value={g}
                          className="sr-only"
                          checked={on}
                          disabled={disabled}
                          aria-label={`${GENDER_LABELS[g]}, ${who}`}
                          onChange={() => update(i, { gender: g })}
                        />
                        {GENDER_LABELS[g]}
                      </label>
                    );
                  })}
                </div>
              </div>
              {problem &&
              (problem.target === `party-name-${i}` ||
                problem.target === `party-age-${i}`) ? (
                <p className="text-sm text-crimson">{problem.message}</p>
              ) : null}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
                {PARTY_NEEDS.map((need) => {
                  const on = p.needs.includes(need);
                  return (
                    <label
                      key={need}
                      className="flex min-h-11 cursor-pointer items-center gap-2.5 text-sm text-ink has-disabled:cursor-not-allowed has-disabled:opacity-50"
                    >
                      <input
                        type="checkbox"
                        className="peer sr-only"
                        checked={on}
                        disabled={disabled}
                        aria-label={`${NEED_LABELS[need]}, ${who}`}
                        onChange={() => toggleNeed(i, need)}
                      />
                      {/* A drawn box with a tick: the same object a printed
                          form has, so it reads as something to tick. */}
                      <span
                        aria-hidden="true"
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-[4px] border-2 transition-colors peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-teal-deep ${on ? "border-magenta bg-magenta text-white" : "border-hairline-strong bg-card"}`}
                      >
                        <svg
                          viewBox="0 0 16 16"
                          className={`h-3 w-3 ${on ? "" : "opacity-0"}`}
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2.4}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M3 8.5l3.2 3L13 4.5" />
                        </svg>
                      </span>
                      {NEED_LABELS[need]}
                    </label>
                  );
                })}
              </div>
            </li>
          );
        })}
      </ul>
      {onSelfIncludedChange ? (
        <label className="mt-5 flex min-h-11 cursor-pointer items-center gap-2.5 text-sm text-ink has-disabled:cursor-not-allowed has-disabled:opacity-50">
          <input
            type="checkbox"
            className="h-5 w-5 shrink-0 accent-magenta"
            checked={!selfIncluded}
            disabled={disabled}
            onChange={(e) => setSelf(!e.target.checked)}
          />
          I am registering others and not coming myself
        </label>
      ) : null}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => add("adult")}
          disabled={disabled || full}
          className="min-h-11 rounded-full border border-hairline-strong px-5 text-sm font-medium text-ink transition-colors hover:border-ink hover:bg-ivory-deep disabled:opacity-50"
        >
          Add an adult
        </button>
        <button
          type="button"
          onClick={() => add("child")}
          disabled={disabled || full}
          className="min-h-11 rounded-full border border-hairline-strong px-5 text-sm font-medium text-ink transition-colors hover:border-ink hover:bg-ivory-deep disabled:opacity-50"
        >
          Add a child
        </button>
        <p className="text-xs text-ink-soft">
          {adults} {adults === 1 ? "adult" : "adults"}, {party.length - adults}{" "}
          {party.length - adults === 1 ? "child" : "children"}
          {full ? ` (the most one registration can hold is ${PARTY_MAX})` : ""}
        </p>
      </div>
    </fieldset>
  );
}
